import { useRef, useState } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Upload, FileSpreadsheet } from "lucide-react";

const COLUMNS = [
  "Facility Name", "Address", "City", "State", "Zip", "Phone",
  "Assigned NP", "Medical Director", "CNA Contact",
  "DOR Name", "DOR Phone", "DOR Email",
  "NP Approval Status", "Start Date", "Patient Count",
  "Visits Per Month", "Visits Per Week", "CPT Codes",
  "Last NP Visit", "Next Meeting", "Claims Status", "Notes",
];

export default function ImportExportPage() {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/export/csv`, {
        method: "GET", credentials: "include",
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "dialog_facilities_export.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch { toast.error("Could not export"); }
    finally { setBusy(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/import/csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported: ${data.created} new · ${data.updated} updated`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-4xl">
      <div className="text-xs uppercase tracking-[0.15em] text-[#8A847A]">Data</div>
      <h1 className="heading text-3xl sm:text-4xl font-bold text-[#292723] mt-2">Import / Export</h1>
      <p className="text-sm text-[#5C5850] mt-1.5 max-w-2xl">
        One row per facility. NPs, Medical Directors and CNAs are matched by name — new ones are created if they don't exist.
        For messier Excel files, just message them to me — I'll clean and load them for you.
      </p>

      <div className="grid md:grid-cols-2 gap-4 mt-8">
        <div className="bg-white border border-[#E5E0D8] rounded-md p-6">
          <Download className="h-5 w-5 text-[#4A6B5D]" />
          <h2 className="heading font-semibold text-lg mt-3">Export CSV</h2>
          <p className="text-sm text-[#5C5850] mt-1">Download every facility as a spreadsheet.</p>
          <Button onClick={download} disabled={busy} className="mt-5 bg-[#4A6B5D] hover:bg-[#3E594D]" data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-2" /> Download CSV
          </Button>
        </div>

        <div className="bg-white border border-[#E5E0D8] rounded-md p-6">
          <Upload className="h-5 w-5 text-[#4A6B5D]" />
          <h2 className="heading font-semibold text-lg mt-3">Import CSV</h2>
          <p className="text-sm text-[#5C5850] mt-1">Bulk add or update facilities.</p>
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={upload} data-testid="import-csv-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={busy} variant="outline" className="mt-5 border-[#E5E0D8]" data-testid="import-csv-btn">
            <Upload className="h-4 w-4 mr-2" /> Choose file
          </Button>
        </div>
      </div>

      <div className="mt-8 bg-white border border-[#E5E0D8] rounded-md p-6">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="h-4 w-4 text-[#4A6B5D]" />
          <h3 className="heading font-semibold">Expected CSV columns</h3>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs font-mono">
          {COLUMNS.map((c) => (<div key={c} className="bg-[#F7F5F0] rounded px-2 py-1.5 text-[#292723]">{c}</div>))}
        </div>
        <p className="text-xs text-[#8A847A] mt-4">CPT Codes are semicolon-separated (e.g. <span className="font-mono">99308; 99309</span>). Approval status: not_started / pending / approved / denied.</p>
      </div>
    </div>
  );
}
